import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { getErrorMessage } from '../../docs/utils/docsUtils';
import { useUpdateTrackedFeature } from '../hooks';
import type { TrackedFeature } from '../types';

interface EditTrackedFeatureDialogProps {
  feature: TrackedFeature | null;
  onClose: () => void;
  onSaved: () => void;
}

const GROUP_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;

const text = {
  cancel: 'Cancel',
  group: 'Group code',
  name: 'Display name',
  save: 'Save',
  title: 'Edit tracked feature',
} as const;

export const EditTrackedFeatureDialog = ({
  feature,
  onClose,
  onSaved,
}: EditTrackedFeatureDialogProps) => {
  const [name, setName] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const update = useUpdateTrackedFeature();

  useEffect(() => {
    setName(feature?.name ?? '');
    setGroupCode(feature?.groupCode ?? '');
    update.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature]);

  const canSave = Boolean(name.trim()) && GROUP_PATTERN.test(groupCode);

  const save = () => {
    if (!feature || !canSave) return;
    update.mutate(
      { publicId: feature.publicId, input: { name: name.trim(), groupCode } },
      {
        onSuccess: () => {
          onSaved();
          onClose();
        },
      }
    );
  };

  return (
    <Dialog open={Boolean(feature)} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{text.title}</DialogTitle>
      <DialogContent className="flex flex-col gap-3 !pt-2">
        <TextField
          size="small"
          label={text.name}
          value={name}
          onChange={(event) => setName(event.target.value)}
          inputProps={{ maxLength: 100 }}
        />
        <TextField
          size="small"
          label={text.group}
          value={groupCode}
          onChange={(event) => setGroupCode(event.target.value.toLowerCase())}
          error={!GROUP_PATTERN.test(groupCode)}
        />
        {update.isError && <Alert severity="error">{getErrorMessage(update.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{text.cancel}</Button>
        <Button variant="contained" onClick={save} disabled={!canSave || update.isPending}>
          {text.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
